import React, { useRef, useEffect, useState } from 'react';
import { Button } from 'reactstrap';

const SignaturePad = ({
  width = 400,
  height = 200,
  lineColor = '#000000',
  lineWidth = 2,
  backgroundColor = '#ffffff',
  clearButtonText = "Limpiar",
  onSignatureChange,
  className = "",
  disabled = false
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Inicializar el canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Configurar el canvas
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [backgroundColor, lineColor, lineWidth]);

  // Prevenir scroll en dispositivos móviles
  const preventScroll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  const startDrawing = (e) => {
    if (disabled) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let x, y;
    
    if (e.type.includes('touch')) {
      x = (e.touches[0].clientX - rect.left) * scaleX;
      y = (e.touches[0].clientY - rect.top) * scaleY;
    } else {
      x = (e.clientX - rect.left) * scaleX;
      y = (e.clientY - rect.top) * scaleY;
    }

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);

    // Prevenir scroll en móviles
    if (e.type.includes('touch')) {
      document.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('touchend', stopDrawingAndCleanup);
      document.addEventListener('touchcancel', stopDrawingAndCleanup);
    }
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let x, y;
    
    if (e.type.includes('touch')) {
      x = (e.touches[0].clientX - rect.left) * scaleX;
      y = (e.touches[0].clientY - rect.top) * scaleY;
    } else {
      x = (e.clientX - rect.left) * scaleX;
      y = (e.clientY - rect.top) * scaleY;
    }

    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawingAndCleanup = () => {
    // Limpiar event listeners
    document.removeEventListener('touchmove', preventScroll);
    document.removeEventListener('touchend', stopDrawingAndCleanup);
    document.removeEventListener('touchcancel', stopDrawingAndCleanup);
    
    stopDrawing();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setIsEmpty(false);
      
      // Notificar que la firma ha cambiado
      if (onSignatureChange) {
        const signatureData = getSignatureData();
        onSignatureChange(signatureData);
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    setIsEmpty(true);
    
    // Notificar que la firma fue limpiada
    if (onSignatureChange) {
      onSignatureChange(null);
    }
  };

  const getSignatureData = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL('image/png');
  };

  // Manejar eventos táctiles para dispositivos móviles
  const handleTouchStart = (e) => {
    e.preventDefault();
    startDrawing(e);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    draw(e);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrawingAndCleanup();
  };

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      document.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('touchend', stopDrawingAndCleanup);
      document.removeEventListener('touchcancel', stopDrawingAndCleanup);
    };
  }, []);

  return (
    <div className={`signature-pad ${className}`}>
      <div 
        className="signature-canvas-container"
        style={{
          border: '2px solid #dee2e6',
          borderRadius: '4px',
          overflow: 'hidden',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          touchAction: 'none' // para prevenir scroll
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            display: 'block',
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'crosshair',
            touchAction: 'none' // para prevenir scroll
          }}
        />
      </div>
      
      <div className="signature-controls mt-2">
        <Button
          color="secondary"
          onClick={clearSignature}
          disabled={isEmpty || disabled}
          size="sm"
        >
          {clearButtonText}
        </Button>
        
        {isEmpty && (
          <small className="text-muted ms-2">
            Dibuje su firma o rúbrica en el área superior
          </small>
        )}
      </div>
    </div>
  );
};

export default SignaturePad;